<template>
    <div class="fb-table">
        <Datatable :tableId="tableId" :columns="columns" :data="data" :showLoading="loading" uniqueField="name"
            :orderColumn="orderColumn" :rowClick="onRowClick" :compact="true" :ignoreCache="true"
            :captializeHeader="false" zeroRecords="This folder is empty" />
    </div>
</template>

<script>
/*
 * The object listing for both file browsers. Folders and files share one row shape: a
 * type glyph and name, the size (blank for folders, which have none), and the last
 * modified time. Clicking a row opens it - a folder descends, a file is handed to the
 * parent, which is the whole interaction in either browser.
 */
import Datatable from "../Datatable.vue";
import { DateTimeFormat, formatBytes } from "../../utils/commonFunction";
import { fileGlyph } from "../../utils/fileBrowser";

export default {
    name: "FileBrowserTable",
    components: { Datatable },
    props: {
        data: {
            type: Array,
            default: () => [],
        },
        loading: {
            type: Boolean,
            default: false,
        },
        tableId: {
            type: String,
            default: "fileBrowser",
        },
    },
    data() {
        return {
            columns: [
                {
                    data: "folderName",
                    title: "Name",
                    render: (data, type, row) => {
                        // Sort and filter on the bare name so the markup is never matched.
                        // Sorting is prefixed by kind so folders group above files at
                        // either direction, the way a directory listing is read.
                        if (type === "sort") return `${row.is_dir ? 0 : 1}${data}`;
                        if (type !== "display") return data;
                        const glyph = fileGlyph(row.name, row.is_dir);
                        const name = this.escape(data || "/");
                        return `<span class="fb-entry"><i class="fa-solid ${glyph.icon} ${glyph.tone} fb-entry-icon"></i><span class="fb-entry-name">${name}</span>${row.is_dir ? '<i class="fa-solid fa-chevron-right fb-entry-chevron"></i>' : ""}</span>`;
                    },
                },
                {
                    data: "size",
                    title: "Size",
                    render: (data, type, row) => {
                        if (type !== "display") return data;
                        // directories carry no size - an explicit dash beats "0 Bytes"
                        return row.is_dir
                            ? '<span class="fb-entry-none">&mdash;</span>'
                            : `<span class="fb-entry-size">${formatBytes(data)}</span>`;
                    },
                },
                {
                    data: "time",
                    title: "Last modified",
                    render: (data, type) => {
                        if (type !== "display") return data;
                        // the service uses the zero time for entries it has no date for
                        return !data || data.startsWith("0001-01-01")
                            ? '<span class="fb-entry-none">&mdash;</span>'
                            : `<span class="fb-entry-time">${DateTimeFormat(data)}</span>`;
                    },
                },
            ],
            orderColumn: [0, "asc"],
        };
    },
    methods: {
        escape(value) {
            const node = document.createElement("span");
            node.textContent = value;
            return node.innerHTML;
        },
        // Datatable reports the index into `data`, which is stable across sorting
        onRowClick(index) {
            const row = this.data[index];
            if (row) this.$emit("open", row);
        },
    },
    emits: ["open"],
};
</script>

<style>
/* Datatable renders its own markup, so these reach into it unscoped */
.fb-table table.dataTable {
    color: var(--fb-text);
}

.fb-table table.dataTable tbody tr {
    cursor: pointer;
    transition: background var(--fb-transition);
}

.fb-table table.dataTable tbody tr:hover {
    background: var(--fb-row-hover);
}

.fb-table table.dataTable thead th {
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.4px;
    text-transform: uppercase;
    color: var(--fb-text-muted);
    border-bottom-color: var(--fb-border);
}

.fb-table table.dataTable tbody td {
    border-top-color: var(--fb-border);
    font-size: 13px;
    vertical-align: middle;
}

.fb-entry {
    display: inline-flex;
    align-items: center;
    gap: 0.625rem;
    min-width: 0;
}

.fb-entry-icon {
    width: 1rem;
    flex: none;
    text-align: center;
    font-size: 0.9rem;
}

.fb-entry-name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.fb-entry-size,
.fb-entry-time {
    color: var(--fb-text-muted);
    font-variant-numeric: tabular-nums;
}

.fb-entry-none {
    color: var(--fb-text-dim);
}

/* Sits quietly until the row is hovered, then points the way in */
.fb-entry-chevron {
    font-size: 0.625rem;
    opacity: 0;
    color: var(--fb-text-dim);
    transition: opacity var(--fb-transition), transform var(--fb-transition);
    transform: translateX(-2px);
}

.fb-table table.dataTable tbody tr:hover .fb-entry-chevron {
    opacity: 1;
    transform: translateX(0);
    color: var(--fb-primary);
}

/* ── DataTables chrome ─────────────────────────────────────────────────────
   The search box, page-length select and pager are rendered by DataTables with
   its own Bootstrap classes, so they are retinted here to the same tokens as
   the rest of the browser rather than left on stock blue. */

.fb-table .dataTables_filter input,
.fb-table .dataTables_length select {
    border: 1px solid var(--fb-border);
    border-radius: var(--fb-radius-sm);
    background: var(--fb-surface-2);
    color: var(--fb-text);
    font-size: 13px;
    padding: 0.35rem 0.6rem;
    transition: border-color var(--fb-transition), box-shadow var(--fb-transition);
}

.fb-table .dataTables_filter input::placeholder {
    color: var(--fb-text-dim);
}

.fb-table .dataTables_filter input:focus,
.fb-table .dataTables_length select:focus {
    outline: none;
    border-color: var(--fb-primary);
    box-shadow: 0 0 0 2px var(--fb-primary-dim);
}

.fb-table .dataTables_length,
.fb-table .dataTables_info,
.fb-table .dataTables_length label {
    color: var(--fb-text-muted);
    font-size: 12px;
}

.fb-table .pagination .page-link {
    border: 1px solid var(--fb-border);
    background: var(--fb-surface-2);
    color: var(--fb-text-muted);
    font-size: 12px;
    transition: color var(--fb-transition), border-color var(--fb-transition), background var(--fb-transition);
}

.fb-table .pagination .page-link:hover {
    color: var(--fb-primary);
    border-color: var(--fb-primary);
    background: var(--fb-primary-dim);
}

.fb-table .pagination .page-item.active .page-link {
    border-color: var(--fb-primary);
    background: var(--fb-primary);
    color: #fff;
}

.fb-table .pagination .page-item.disabled .page-link {
    opacity: 0.5;
    background: var(--fb-surface-2);
}

/* the empty-folder message should read as a state, not a stray table row */
.fb-table td.dataTables_empty {
    padding: 2.5rem 1rem;
    color: var(--fb-text-muted);
    font-size: 13px;
    text-align: center;
}
</style>
